import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  Output,
  SimpleChanges,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { FlexService } from '@app/flex-framework/services/flex-framework.service';
import { TargetsService } from '@app/shared/services/targets.service';
import { Target, TargetProperty } from '@app/target/target-api.model';

import { EventEmitter } from '@angular/core';
import { ResourcePrivacyLevel } from '@app/shared/models/core-api.model';
import { ModalService } from '@app/shared/services/modal.service';
import { UserSearchModalComponent } from '@app/user/components/user-search-modal/user-search-modal.component';
import { UserSearchItem } from '@app/user/user-api.model';
import { switchMap, tap } from 'rxjs/operators';
import { AuthService } from '@app/shared/services/auth.service';

@Component({
  selector: 'dgx-flex-meta-data',
  templateUrl: './flex-meta-data.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlexMetaDataComponent {
  public pseudoMetaData: TargetProperty;
  public privacyLevel: string;
  public i18n = this.translate.instant([
    'Core_LastUpdated',
    'Core_Collaborators',
    'FlexCtrl_AddProperties',
    'FlexCtrl_EditProperties',
    'FlexCtrl_PrimaryContact',
    'FlexCtrl_AddPrimaryContact',
    'FlexCtrl_EditPrimaryContact',
    'FlexCtrl_SavePrimaryContact',
  ]);

  @Input() public target: Target;
  @Input() public isEditing: boolean;
  @Input() public isPlan: boolean;
  @Output() public editCollaborators?: EventEmitter<any> = new EventEmitter();
  @Output() public editPrivacyLevel?: EventEmitter<any> = new EventEmitter();

  constructor(
    private authService: AuthService,
    private cdr: ChangeDetectorRef,
    private flexService: FlexService,
    private modalService: ModalService,
    private targetsService: TargetsService,
    private translate: TranslateService
  ) {}

  public get hideCollaborators() {
    return (
      (!!this.authService.authUser?.defaultOrgInfo?.settings
        ?.disableCollaboratorsHeader &&
        !this.isEditing) ||
      this.target.targetType === 'Browse'
    );
  }

  public get metaIsEmpty() {
    return this.target.metadata?.length === 0;
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.target && changes.target.currentValue) {
      this.privacyLevel = this.getPrivacyString(
        this.target.privacyId,
        this.target.groupIds?.length
      );
    }
  }

  public modifyMetadata() {
    return this.flexService
      .metadataModal(this.target)
      .pipe(
        switchMap((result: Target) => {
          this.target.metadata = result.metadata;
          this.target.targetType = result.targetType;
          this.cdr.detectChanges();
          return this.targetsService.updateTarget(result);
        })
      )
      .subscribe();
  }

  public editPrimaryContact() {
    return this.modalService
      .show<UserSearchItem[]>(UserSearchModalComponent, {
        inputs: {
          users: this.target.primaryContact,
          modalHeaderText: this.i18n.FlexCtrl_AddPrimaryContact,
          submitButtonText: this.i18n.FlexCtrl_SavePrimaryContact,
          ariaLabel: this.i18n.FlexCtrl_PrimaryContact,
          singular: true,
        },
      })
      .pipe(
        switchMap((selectedContact) => {
          const updatedTarget: Target = {
            ...this.target,
            primaryContact: selectedContact[0],
            primaryContactUserKey: selectedContact[0]?.userProfileKey,
          };
          this.target = updatedTarget;
          this.cdr.detectChanges();
          return this.targetsService.updateTarget(updatedTarget);
        }),
        tap(() => {
          // track primary contact?
        })
      )
      .subscribe();
  }

  public onEditCollaborators() {
    this.editCollaborators.emit();
  }

  /** Gets the correct privacy string for this target
   * Helpful info what the concatenated strings return:
   * @return
   * "Core_PrivacyLevel0":"Private to collaborators"
   * "Core_PrivacyLevel1":"Visible to organization",
   * "Core_PrivacyLevel2":"Visible to a specific group",
   * "Core_PrivacyLevel2A":"Visible to {{groupCount}} group",
   * "Core_PrivacyLevel2B":"Visible to {{groupCount}} groups",
   * "Core_PrivacyLevel2C":"Visible to {{groupName}}",
   * "Core_PrivacyLevel3":"Visible on collaborators\u0027 profiles"
   */
  private getPrivacyString(
    privacyId: ResourcePrivacyLevel,
    groupCount: number
  ): string {
    const privacyGroupsType =
      privacyId === 2 ? (groupCount === 1 ? 'A' : 'B') : '';

    const privacyLevelResource =
      'Core_PrivacyLevel' + privacyId + privacyGroupsType;

    return this.translate.instant(privacyLevelResource, {
      groupCount: groupCount,
    });
  }
}
